/**
 * Theming and formatting for an embedded dashboard.
 *
 * ## Why a token object instead of Tailwind classes
 *
 * The Barnyard UI styles everything with Tailwind utility classes bound to its own design
 * tokens (`bg-surfaceAlt`, `text-textMuted`, …). Those class names only mean anything inside
 * that app's Tailwind config. A component that ships them and is dropped into the CNC frontend
 * renders as unstyled HTML — and unstyled is the *good* case; the bad case is a host that
 * happens to define `bg-surface` differently and renders something subtly, confusingly wrong.
 *
 * So anything built on this kit styles itself with inline styles derived from a token object. A
 * host that wants it to match passes a partial `theme`; a host that passes nothing gets a
 * neutral, self-consistent look that works on a light background. This is the price of "plug and
 * play" and it is a low one: the whole token set is 14 values.
 */
export const defaultTheme = {
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    surface: '#ffffff',
    surfaceAlt: '#f7f8fa',
    surfaceHover: '#eef0f4',
    border: '#dfe3e8',
    textPrimary: '#1a1d21',
    textSecondary: '#4a515a',
    textMuted: '#8a919b',
    accent: '#0b6bcb',
    accentText: '#ffffff',
    success: '#1c7c46',
    warning: '#a56300',
    danger: '#b3261e',
    radius: '8px',
};
export function resolveTheme(overrides) {
    return { ...defaultTheme, ...(overrides ?? {}) };
}
export function toneColor(tone, theme) {
    switch (tone) {
        case 'success':
            return theme.success;
        case 'warning':
            return theme.warning;
        case 'danger':
            return theme.danger;
        case 'muted':
            return theme.textMuted;
        case 'accent':
        default:
            return theme.accent;
    }
}
/**
 * Build a status→colour function from a domain's own vocabulary.
 *
 * The point of taking a map rather than a `switch` is that the mapping becomes data the owning
 * package states once and the kit never has to know about. `stateColor` in the mission tracker
 * and a ghost-town/overpriced/healthy mapping in the FinOps dashboard are then the same code
 * with two tables, and neither can drift from the token set.
 */
export function makeStatusColor(map, fallback = 'warning') {
    return (status, theme) => toneColor(map[status] ?? fallback, theme);
}
/**
 * Human-readable duration. Written for someone reading "how long have I been waiting", not for
 * a metrics dashboard.
 */
export function formatDuration(secs) {
    if (secs < 60)
        return `${Math.max(0, Math.round(secs))}s`;
    const mins = Math.floor(secs / 60);
    if (mins < 60)
        return `${mins}m ${Math.round(secs % 60)}s`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m`;
}
export function timeAgo(iso, now = Date.now()) {
    const diffSecs = Math.max(0, (now - new Date(iso).getTime()) / 1000);
    if (diffSecs < 45)
        return 'just now';
    return `${formatDuration(diffSecs)} ago`;
}
/**
 * Money, rendered so that two figures on the same screen are comparable.
 *
 * `maximumFractionDigits: 0` above $100 on purpose: a monthly spend of "$5,231" and one of
 * "$5,230.94" carry the same decision, and the cents are noise that makes a column harder to
 * scan. Below $100 the cents can be the whole signal, so they are kept.
 */
export function formatCurrency(value, currency = 'USD', locale = 'en-US') {
    const abs = Math.abs(value);
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: abs < 100 ? 2 : 0,
        maximumFractionDigits: abs < 100 ? 2 : 0,
    }).format(value);
}
/** Thousands-separated integer — for session and request counts. */
export function formatCount(value, locale = 'en-US') {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}
//# sourceMappingURL=theme.js.map